/**
 * FikoSfM - Structure from Motion
 *
 * Monoküler kameradan 3D yapı çıkarma:
 * - Feature detection ve tracking
 * - Multi-view triangulation
 * - Bundle adjustment (basitleştirilmiş)
 *
 * Bu, tek frame depth estimation'dan çok daha doğru sonuç verir.
 */

import * as THREE from 'three';

/** Tracked feature point */
export interface TrackedFeature {
    id: number;
    // 2D position in current frame (normalized image coords)
    x: number;
    y: number;
    // Feature descriptor (for matching)
    descriptor: number[];
    // 3D position (triangulated, null if not yet triangulated)
    position3D: THREE.Vector3 | null;
    // Frames where this feature was seen
    observations: FeatureObservation[];
    // Quality score
    quality: number;
}

/** Single observation of a feature */
interface FeatureObservation {
    frameIndex: number;
    x: number;
    y: number;
    // Camera pose at this frame
    cameraPose: CameraPose;
}

/** Camera pose for a frame */
interface CameraPose {
    rotation: THREE.Matrix4;
    translation: THREE.Vector3;
    intrinsics: CameraIntrinsics;
}

/** Camera intrinsics */
interface CameraIntrinsics {
    fx: number;
    fy: number;
    cx: number;
    cy: number;
}

/** Triangulated 3D point with color */
export interface SfMPoint {
    position: THREE.Vector3;
    color: THREE.Color;
    confidence: number;
    observationCount: number;
}

/**
 * FikoSfM - Structure from Motion Engine
 */
export class FikoSfM {
    // Feature tracking
    private features: Map<number, TrackedFeature> = new Map();
    private nextFeatureId: number = 0;
    private frameIndex: number = 0;

    // Previous frame data for tracking
    private prevGray: Float32Array | null = null;
    private prevWidth: number = 0;
    private prevHeight: number = 0;

    // Triangulated points
    private points3D: SfMPoint[] = [];

    // Configuration
    private config = {
        maxFeatures: 500,
        minFeatureDistance: 10,  // pixels
        minObservationsForTriangulation: 3,
        maxReprojectionError: 5.0,  // pixels
        minParallax: 2.0,  // degrees
        featureQualityThreshold: 0.3
    };

    // Camera intrinsics (will be set from frame)
    private intrinsics: CameraIntrinsics = {
        fx: 1000,
        fy: 1000,
        cx: 320,
        cy: 240
    };

    constructor() {}

    /**
     * Process a new frame
     *
     * @param frame ImageData from camera
     * @param cameraPose Camera pose (from VIO or marker tracking)
     * @returns New triangulated points
     */
    processFrame(
        frame: ImageData,
        cameraPose: { rotation: THREE.Matrix4; translation: THREE.Vector3 }
    ): SfMPoint[] {
        const width = frame.width;
        const height = frame.height;

        // Update intrinsics based on frame size
        this.intrinsics = {
            fx: Math.max(width, height),
            fy: Math.max(width, height),
            cx: width / 2,
            cy: height / 2
        };

        // Convert to grayscale
        const gray = this.toGrayscale(frame);

        const pose: CameraPose = {
            rotation: cameraPose.rotation.clone(),
            translation: cameraPose.translation.clone(),
            intrinsics: { ...this.intrinsics }
        };

        let newPoints: SfMPoint[] = [];

        if (this.prevGray && this.prevWidth === width && this.prevHeight === height) {
            // Track existing features
            this.trackFeatures(this.prevGray, gray, width, height, pose);

            // Detect new features if needed
            if (this.features.size < this.config.maxFeatures * 0.7) {
                this.detectNewFeatures(gray, frame, width, height, pose);
            }

            // Triangulate features with enough observations
            newPoints = this.triangulateFeatures();

            // Remove lost features
            this.pruneFeatures();
        } else {
            // First frame - just detect features
            this.detectNewFeatures(gray, frame, width, height, pose);
        }

        // Update previous frame
        this.prevGray = gray;
        this.prevWidth = width;
        this.prevHeight = height;
        this.frameIndex++;

        return newPoints;
    }

    /**
     * Get all triangulated points
     */
    getPoints(): SfMPoint[] {
        return this.points3D;
    }

    /**
     * Clear all data
     */
    clear(): void {
        this.features.clear();
        this.points3D = [];
        this.prevGray = null;
        this.frameIndex = 0;
        this.nextFeatureId = 0;
    }

    // ========== FEATURE DETECTION ==========

    /**
     * Detect new features using Harris corner detection
     */
    private detectNewFeatures(
        gray: Float32Array,
        frame: ImageData,
        width: number,
        height: number,
        pose: CameraPose
    ): void {
        // Harris corner response
        const harris = this.computeHarrisResponse(gray, width, height);

        // Non-maximum suppression to find corners
        const corners = this.nonMaxSuppression(harris, width, height, this.config.minFeatureDistance);

        // Sort by response strength
        corners.sort((a, b) => b.response - a.response);

        // Add new features (avoiding existing ones)
        let added = 0;
        const maxToAdd = this.config.maxFeatures - this.features.size;

        for (const corner of corners) {
            if (added >= maxToAdd) break;

            // Check distance to existing features
            let tooClose = false;
            for (const feature of this.features.values()) {
                const dx = corner.x - feature.x * width;
                const dy = corner.y - feature.y * height;
                if (dx * dx + dy * dy < this.config.minFeatureDistance * this.config.minFeatureDistance) {
                    tooClose = true;
                    break;
                }
            }

            if (!tooClose) {
                // Create new feature
                const descriptor = this.computeDescriptor(gray, corner.x, corner.y, width, height);

                // Get color from original image
                const pixelIdx = (Math.floor(corner.y) * width + Math.floor(corner.x)) * 4;
                const r = frame.data[pixelIdx] / 255;
                const g = frame.data[pixelIdx + 1] / 255;
                const b = frame.data[pixelIdx + 2] / 255;

                const feature: TrackedFeature = {
                    id: this.nextFeatureId++,
                    x: corner.x / width,  // Normalized coords
                    y: corner.y / height,
                    descriptor,
                    position3D: null,
                    observations: [{
                        frameIndex: this.frameIndex,
                        x: corner.x / width,
                        y: corner.y / height,
                        cameraPose: pose
                    }],
                    quality: corner.response
                };

                // Store color for later triangulation
                (feature as any).color = new THREE.Color(r, g, b);

                this.features.set(feature.id, feature);
                added++;
            }
        }
    }

    /**
     * Compute Harris corner response
     */
    private computeHarrisResponse(gray: Float32Array, width: number, height: number): Float32Array {
        const response = new Float32Array(width * height);

        // Compute gradients
        const Ix = new Float32Array(width * height);
        const Iy = new Float32Array(width * height);

        for (let y = 1; y < height - 1; y++) {
            for (let x = 1; x < width - 1; x++) {
                const idx = y * width + x;
                Ix[idx] = (gray[idx + 1] - gray[idx - 1]) / 2;
                Iy[idx] = (gray[idx + width] - gray[idx - width]) / 2;
            }
        }

        // Harris response: det(M) - k * trace(M)^2
        const k = 0.04;
        const windowSize = 3;
        const half = Math.floor(windowSize / 2);

        for (let y = half + 1; y < height - half - 1; y++) {
            for (let x = half + 1; x < width - half - 1; x++) {
                let Ixx = 0, Iyy = 0, Ixy = 0;

                for (let wy = -half; wy <= half; wy++) {
                    for (let wx = -half; wx <= half; wx++) {
                        const idx = (y + wy) * width + (x + wx);
                        Ixx += Ix[idx] * Ix[idx];
                        Iyy += Iy[idx] * Iy[idx];
                        Ixy += Ix[idx] * Iy[idx];
                    }
                }

                const det = Ixx * Iyy - Ixy * Ixy;
                const trace = Ixx + Iyy;
                response[y * width + x] = det - k * trace * trace;
            }
        }

        return response;
    }

    /**
     * Non-maximum suppression
     */
    private nonMaxSuppression(
        response: Float32Array,
        width: number,
        height: number,
        minDistance: number
    ): { x: number; y: number; response: number }[] {
        const corners: { x: number; y: number; response: number }[] = [];
        const half = Math.floor(minDistance / 2);

        // Find local maxima
        for (let y = half; y < height - half; y++) {
            for (let x = half; x < width - half; x++) {
                const idx = y * width + x;
                const val = response[idx];

                if (val < 0.001) continue;  // Threshold

                let isMax = true;
                for (let wy = -half; wy <= half && isMax; wy++) {
                    for (let wx = -half; wx <= half && isMax; wx++) {
                        if (wx === 0 && wy === 0) continue;
                        if (response[(y + wy) * width + (x + wx)] > val) {
                            isMax = false;
                        }
                    }
                }

                if (isMax) {
                    corners.push({ x, y, response: val });
                }
            }
        }

        return corners;
    }

    /**
     * Compute simple descriptor (patch intensity)
     */
    private computeDescriptor(
        gray: Float32Array,
        x: number,
        y: number,
        width: number,
        height: number
    ): number[] {
        const patchSize = 7;
        const half = Math.floor(patchSize / 2);
        const descriptor: number[] = [];

        const ix = Math.floor(x);
        const iy = Math.floor(y);

        for (let py = -half; py <= half; py++) {
            for (let px = -half; px <= half; px++) {
                const sx = Math.max(0, Math.min(width - 1, ix + px));
                const sy = Math.max(0, Math.min(height - 1, iy + py));
                descriptor.push(gray[sy * width + sx]);
            }
        }

        // Normalize
        const mean = descriptor.reduce((a, b) => a + b, 0) / descriptor.length;
        const std = Math.sqrt(descriptor.reduce((a, b) => a + (b - mean) ** 2, 0) / descriptor.length) || 1;

        return descriptor.map(v => (v - mean) / std);
    }

    // ========== FEATURE TRACKING ==========

    /**
     * Track features using Lucas-Kanade optical flow
     */
    private trackFeatures(
        prevGray: Float32Array,
        currGray: Float32Array,
        width: number,
        height: number,
        pose: CameraPose
    ): void {
        const windowSize = 21;
        const half = Math.floor(windowSize / 2);
        const maxIterations = 30;
        const epsilon = 0.01;

        for (const feature of this.features.values()) {
            // Convert normalized coords to pixels
            let px = feature.x * width;
            let py = feature.y * height;

            // Lucas-Kanade iteration
            for (let iter = 0; iter < maxIterations; iter++) {
                const ix = Math.floor(px);
                const iy = Math.floor(py);

                if (ix < half || ix >= width - half || iy < half || iy >= height - half) {
                    // Out of bounds - mark for removal
                    feature.quality = -1;
                    break;
                }

                // Compute gradients and temporal difference
                let Ixx = 0, Iyy = 0, Ixy = 0;
                let Ixt = 0, Iyt = 0;

                for (let wy = -half; wy <= half; wy++) {
                    for (let wx = -half; wx <= half; wx++) {
                        const prevIdx = (iy + wy) * width + (ix + wx);
                        const currIdx = prevIdx;

                        const Ix = (prevGray[prevIdx + 1] - prevGray[prevIdx - 1]) / 2;
                        const Iy = (prevGray[prevIdx + width] - prevGray[prevIdx - width]) / 2;
                        const It = currGray[currIdx] - prevGray[prevIdx];

                        Ixx += Ix * Ix;
                        Iyy += Iy * Iy;
                        Ixy += Ix * Iy;
                        Ixt += Ix * It;
                        Iyt += Iy * It;
                    }
                }

                // Solve 2x2 system
                const det = Ixx * Iyy - Ixy * Ixy;
                if (Math.abs(det) < 0.0001) {
                    feature.quality *= 0.5;  // Poor tracking
                    break;
                }

                const dx = (Iyy * Ixt - Ixy * Iyt) / det;
                const dy = (Ixx * Iyt - Ixy * Ixt) / det;

                px -= dx;
                py -= dy;

                if (dx * dx + dy * dy < epsilon * epsilon) {
                    break;  // Converged
                }
            }

            // Validate tracking
            if (feature.quality > 0) {
                const newX = px / width;
                const newY = py / height;

                // Check if within bounds
                if (newX >= 0 && newX < 1 && newY >= 0 && newY < 1) {
                    // Compare descriptors
                    const newDesc = this.computeDescriptor(currGray, px, py, width, height);
                    const similarity = this.compareDescriptors(feature.descriptor, newDesc);

                    if (similarity > 0.5) {
                        // Good match - update feature
                        feature.x = newX;
                        feature.y = newY;
                        feature.descriptor = newDesc;
                        feature.observations.push({
                            frameIndex: this.frameIndex,
                            x: newX,
                            y: newY,
                            cameraPose: pose
                        });
                        feature.quality = Math.min(1, feature.quality + 0.1);
                    } else {
                        feature.quality -= 0.3;
                    }
                } else {
                    feature.quality = -1;  // Out of frame
                }
            }
        }
    }

    /**
     * Compare two descriptors (normalized cross-correlation)
     */
    private compareDescriptors(desc1: number[], desc2: number[]): number {
        if (desc1.length !== desc2.length) return 0;

        let sum = 0;
        for (let i = 0; i < desc1.length; i++) {
            sum += desc1[i] * desc2[i];
        }

        return sum / desc1.length;
    }

    // ========== TRIANGULATION ==========

    /**
     * Triangulate features with enough observations
     */
    private triangulateFeatures(): SfMPoint[] {
        const newPoints: SfMPoint[] = [];

        for (const feature of this.features.values()) {
            // Skip if already triangulated or not enough observations
            if (feature.position3D !== null) continue;
            if (feature.observations.length < this.config.minObservationsForTriangulation) continue;

            // Check parallax (baseline angle)
            const parallax = this.computeParallax(feature.observations);
            if (parallax < this.config.minParallax) continue;

            // Triangulate using first and last observation
            const first = feature.observations[0];
            const last = feature.observations[feature.observations.length - 1];

            const point3D = this.triangulatePoint(
                first.x, first.y, first.cameraPose,
                last.x, last.y, last.cameraPose
            );

            if (point3D) {
                // Validate with reprojection error
                const avgError = this.computeReprojectionError(point3D, feature.observations);

                if (avgError < this.config.maxReprojectionError) {
                    feature.position3D = point3D;

                    const sfmPoint: SfMPoint = {
                        position: point3D,
                        color: (feature as any).color || new THREE.Color(0.5, 0.5, 0.5),
                        confidence: Math.min(1, 1 / (avgError + 0.1)),
                        observationCount: feature.observations.length
                    };

                    this.points3D.push(sfmPoint);
                    newPoints.push(sfmPoint);
                }
            }
        }

        return newPoints;
    }

    /**
     * Compute parallax angle between observations
     */
    private computeParallax(observations: FeatureObservation[]): number {
        if (observations.length < 2) return 0;

        const first = observations[0].cameraPose.translation;
        const last = observations[observations.length - 1].cameraPose.translation;

        const baseline = first.distanceTo(last);
        if (baseline < 1) return 0;  // Less than 1mm baseline

        // Approximate depth from first observation
        const depth = 300;  // mm estimate

        // Parallax angle in degrees
        return Math.atan2(baseline, depth) * 180 / Math.PI;
    }

    /**
     * Triangulate a 3D point from two observations
     *
     * Uses DLT (Direct Linear Transform) method
     */
    private triangulatePoint(
        x1: number, y1: number, pose1: CameraPose,
        x2: number, y2: number, pose2: CameraPose
    ): THREE.Vector3 | null {
        // Build projection matrices
        const P1 = this.buildProjectionMatrix(pose1);
        const P2 = this.buildProjectionMatrix(pose2);

        // Convert normalized coords to pixel coords
        const u1 = x1 * pose1.intrinsics.fx * 2 + pose1.intrinsics.cx;
        const v1 = y1 * pose1.intrinsics.fy * 2 + pose1.intrinsics.cy;
        const u2 = x2 * pose2.intrinsics.fx * 2 + pose2.intrinsics.cx;
        const v2 = y2 * pose2.intrinsics.fy * 2 + pose2.intrinsics.cy;

        // DLT: build A matrix for Ax = 0
        // Each observation gives 2 equations
        const A = [
            [u1 * P1[2][0] - P1[0][0], u1 * P1[2][1] - P1[0][1], u1 * P1[2][2] - P1[0][2], u1 * P1[2][3] - P1[0][3]],
            [v1 * P1[2][0] - P1[1][0], v1 * P1[2][1] - P1[1][1], v1 * P1[2][2] - P1[1][2], v1 * P1[2][3] - P1[1][3]],
            [u2 * P2[2][0] - P2[0][0], u2 * P2[2][1] - P2[0][1], u2 * P2[2][2] - P2[0][2], u2 * P2[2][3] - P2[0][3]],
            [v2 * P2[2][0] - P2[1][0], v2 * P2[2][1] - P2[1][1], v2 * P2[2][2] - P2[1][2], v2 * P2[2][3] - P2[1][3]]
        ];

        // Solve using SVD (simplified - use pseudo-inverse)
        const point = this.solveDLT(A);
        if (!point) return null;

        // Check if point is in front of both cameras
        if (point.z < 50 || point.z > 2000) return null;  // 50mm - 2000mm range

        return point;
    }

    /**
     * Build 3x4 projection matrix from camera pose
     */
    private buildProjectionMatrix(pose: CameraPose): number[][] {
        const K = [
            [pose.intrinsics.fx, 0, pose.intrinsics.cx],
            [0, pose.intrinsics.fy, pose.intrinsics.cy],
            [0, 0, 1]
        ];

        // Extract rotation matrix elements
        const R = pose.rotation.elements;
        const t = pose.translation;

        // R is column-major, convert to row-major for 3x3
        const Rmat = [
            [R[0], R[4], R[8]],
            [R[1], R[5], R[9]],
            [R[2], R[6], R[10]]
        ];

        // [R|t] 3x4 extrinsic matrix
        const Rt = [
            [Rmat[0][0], Rmat[0][1], Rmat[0][2], t.x],
            [Rmat[1][0], Rmat[1][1], Rmat[1][2], t.y],
            [Rmat[2][0], Rmat[2][1], Rmat[2][2], t.z]
        ];

        // P = K * [R|t]
        const P: number[][] = [[0, 0, 0, 0], [0, 0, 0, 0], [0, 0, 0, 0]];
        for (let i = 0; i < 3; i++) {
            for (let j = 0; j < 4; j++) {
                for (let k = 0; k < 3; k++) {
                    P[i][j] += K[i][k] * Rt[k][j];
                }
            }
        }

        return P;
    }

    /**
     * Solve DLT system using simplified SVD
     */
    private solveDLT(A: number[][]): THREE.Vector3 | null {
        // For 4x4 system, find null space of A^T * A
        // Simplified: use least squares

        const ATA = [[0, 0, 0, 0], [0, 0, 0, 0], [0, 0, 0, 0], [0, 0, 0, 0]];

        // Compute A^T * A
        for (let i = 0; i < 4; i++) {
            for (let j = 0; j < 4; j++) {
                for (let k = 0; k < 4; k++) {
                    ATA[i][j] += A[k][i] * A[k][j];
                }
            }
        }

        // Power iteration to find smallest eigenvector
        let x = [1, 1, 1, 1];
        const iterations = 50;

        // Find largest eigenvalue first
        for (let iter = 0; iter < iterations; iter++) {
            const y = [0, 0, 0, 0];
            for (let i = 0; i < 4; i++) {
                for (let j = 0; j < 4; j++) {
                    y[i] += ATA[i][j] * x[j];
                }
            }

            // Normalize
            const norm = Math.sqrt(y[0] * y[0] + y[1] * y[1] + y[2] * y[2] + y[3] * y[3]);
            if (norm < 0.0001) return null;

            for (let i = 0; i < 4; i++) {
                x[i] = y[i] / norm;
            }
        }

        // x is now approximation of smallest eigenvector
        // Dehomogenize
        if (Math.abs(x[3]) < 0.0001) return null;

        return new THREE.Vector3(
            x[0] / x[3],
            x[1] / x[3],
            x[2] / x[3]
        );
    }

    /**
     * Compute average reprojection error
     */
    private computeReprojectionError(point3D: THREE.Vector3, observations: FeatureObservation[]): number {
        let totalError = 0;

        for (const obs of observations) {
            // Project 3D point to 2D
            const p = point3D.clone();

            // Apply camera transform
            p.applyMatrix4(obs.cameraPose.rotation);
            p.add(obs.cameraPose.translation);

            // Project
            if (p.z <= 0) return Infinity;

            const projX = (p.x / p.z) * obs.cameraPose.intrinsics.fx + obs.cameraPose.intrinsics.cx;
            const projY = (p.y / p.z) * obs.cameraPose.intrinsics.fy + obs.cameraPose.intrinsics.cy;

            // Convert observation to pixels
            const obsX = obs.x * obs.cameraPose.intrinsics.fx * 2 + obs.cameraPose.intrinsics.cx;
            const obsY = obs.y * obs.cameraPose.intrinsics.fy * 2 + obs.cameraPose.intrinsics.cy;

            // Error in pixels
            const error = Math.sqrt((projX - obsX) ** 2 + (projY - obsY) ** 2);
            totalError += error;
        }

        return totalError / observations.length;
    }

    // ========== CLEANUP ==========

    /**
     * Remove lost features
     */
    private pruneFeatures(): void {
        for (const [id, feature] of this.features.entries()) {
            if (feature.quality < this.config.featureQualityThreshold) {
                this.features.delete(id);
            }
        }
    }

    // ========== UTILITIES ==========

    /**
     * Convert to grayscale
     */
    private toGrayscale(image: ImageData): Float32Array {
        const gray = new Float32Array(image.width * image.height);

        for (let i = 0; i < gray.length; i++) {
            const idx = i * 4;
            gray[i] = (
                image.data[idx] * 0.299 +
                image.data[idx + 1] * 0.587 +
                image.data[idx + 2] * 0.114
            ) / 255;
        }

        return gray;
    }

    /**
     * Get tracking stats
     */
    getStats(): {
        trackedFeatures: number;
        triangulatedPoints: number;
        frameCount: number;
    } {
        return {
            trackedFeatures: this.features.size,
            triangulatedPoints: this.points3D.length,
            frameCount: this.frameIndex
        };
    }
}
